import React, { useState, useEffect } from 'react';
import useEscapeKey from '../hooks/useEscapeKey';

// Import utilities and hooks
import { TABS } from './AdminPanel/utils/constants';
import { 
  useEmployees, 
  usePolicies, 
  useRequests, 
  useReports, 
  useAdjustments, 
  useDigest 
} from './AdminPanel/hooks/useAdminData';

// Import components
import Navigation from './AdminPanel/components/Navigation';
import EmployeesTab from './AdminPanel/components/EmployeesTab';
import ReportsTab from './AdminPanel/components/ReportsTab';
import DigestTab from './AdminPanel/components/DigestTab';
import RequestsTab from './AdminPanel/components/RequestsTab';
import { PoliciesTab, AdjustmentsTab } from './AdminPanel/components/OtherTabs';
import OnboardingTab from './AdminPanel/components/OnboardingTab';
import OnboardingOverviewTab from './AdminPanel/components/OnboardingOverviewTab';
import ReferralsTab from './AdminPanel/components/ReferralsTab';
import ProbationPoliciesTab from './AdminPanel/components/ProbationPoliciesTab';
import ProbationExpiriesTab from './AdminPanel/components/ProbationExpiriesTab';
import DismissedTab from './AdminPanel/components/DismissedTab';

export default function AdminPanel() {
  const [activeTab, setActiveTab] = useState(TABS.REPORTS);
  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);

  // Load user from localStorage on mount
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
      }
    } catch (error) {
      console.error('Error loading user from localStorage:', error);
    } finally {
      setUserLoading(false);
    }
  }, []);

  // Data hooks - only run for active tab to prevent overwhelming the database
  const { employees, setEmployees, loading: employeesLoading, error: employeesError } = useEmployees(user);
  const { policies, setPolicies, loading: policiesLoading } = usePolicies(user, activeTab);
  
  // Always call hooks but let them handle conditional logic internally
  const { requests, upcomingRequests, pastRequests, setRequests, setUpcomingRequests, setPastRequests, loading: requestsLoading } = 
    useRequests(user, activeTab);
  
  const { reports, loading: reportsLoading } = 
    useReports(user, activeTab);
  
  const { adjustments, setAdjustments, loading: adjustmentsLoading } = 
    useAdjustments(user, activeTab);
  
  
  const { digestText, loading: digestLoading, fetchDigest } = 
    useDigest(user, activeTab, null);

  // Handle escape key
  useEscapeKey(() => {
    // Close any open modals here if needed
  });

  // Show loading state while user is being loaded
  if (userLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Show error if user is not available
  if (!user) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh'
      }}>
        <div>Please log in to access the admin panel.</div>
      </div>
    );
  }

  // Render the appropriate tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case TABS.EMPLOYEES:
        if (employeesError) {
          return <div style={{ color: 'red', padding: '20px' }}>Error loading employees: {employeesError}</div>;
        }
        return (
          <EmployeesTab 
            employees={employees}
            setEmployees={setEmployees}
            policies={policies}
            user={user}
            loading={employeesLoading}
          />
        );
      case TABS.REQUESTS:
        return (
          <RequestsTab 
            requests={requests}
            upcomingRequests={upcomingRequests}
            pastRequests={pastRequests}
            setRequests={setRequests}
            setUpcomingRequests={setUpcomingRequests}
            setPastRequests={setPastRequests}
            policies={policies}
            loading={requestsLoading}
            user={user}
          />
        );
      case TABS.POLICIES:
        return (
          <PoliciesTab 
            policies={policies}
            user={user}
            setPolicies={setPolicies}
          >
            <button className="primary-button">+ Add Policy</button>
          </PoliciesTab>
        );
      case TABS.ADJUSTMENTS:
        return (
          <AdjustmentsTab 
            adjustments={adjustments}
            loading={adjustmentsLoading}
            user={user}
            setAdjustments={setAdjustments}
            policies={policies}
          >
            <button className="primary-button">+ Add Adjustment</button>
          </AdjustmentsTab>
        );
      case TABS.REPORTS:
        return (
          <ReportsTab 
            reports={reports}
            loading={reportsLoading}
            user={user}
          />
        );
      case TABS.DIGEST:
        return (
          <DigestTab 
            digestText={digestText}
            digestLoading={digestLoading}
            fetchDigest={fetchDigest}
            user={user}
          />
        );
      case TABS.ONBOARDING:
        return (
          <OnboardingTab 
            user={user}
          />
        );
      case TABS.ONBOARDING_OVERVIEW:
        return (
          <OnboardingOverviewTab 
            user={user}
          />
        );
      case TABS.REFERRALS:
        return (
          <ReferralsTab 
            user={user}
          />
        );
      case TABS.PROBATION_POLICIES:
        return (
          <ProbationPoliciesTab 
            user={user}
          />
        );
      case TABS.PROBATION_EXPIRIES:
        return (
          <ProbationExpiriesTab 
            user={user}
          />
        );
      case TABS.DISMISSED:
        return (
          <DismissedTab
            user={user}
          />
        );
      default:
        return <div>Select a tab</div>;
    }
  };

  return (
    <div className="page-wrap">
      <div>
        <Navigation 
          activeTab={activeTab} 
          setActiveTab={setActiveTab} 
          user={user}
        />
        {renderTabContent()}
      </div>
    </div>
  );
} 